import React, { useEffect, useState } from 'react';
import { Analytics, CallMetricsEventEnum, ConferenceTypeEnum, UserTypeEnum } from '@matap/shared';
import { CameraView } from '@/components/compose/call/camera.view';
import styled from 'styled-components/native';
import { Box } from '@/components/base/box';
import { AppTextView } from '@/components/base/app.text.view';
import { AppLottieView } from '@/components/base/app.lottie.view';
import { useUser } from '@/hooks/use.user';
import { useAssets } from '@/hooks/use.assets';
import { AppAvatar } from '../app.avatar';
import { AnswerButtonsView } from './answer.buttons.view';
import { useSocket } from '@/hooks/use.socket';
import { hpVal } from '../../../utils';
import { P2PRoom } from '@/webrtc/p2p.room';

interface Props {
  incoming: boolean;
  connection: P2PRoom;
}
function BaseCallWaitingView({ connection, incoming }: Props) {
  const { userState, userType } = useUser();
  const imageUrl = userType === UserTypeEnum.PATIENT ? userState.visit.doctor.imageFile?.metadata.url : userState.visit.patient.imageFile?.metadata.url;
  const name = userType === UserTypeEnum.PATIENT ? userState.visit.doctor.name : userState.visit.patient.name;
  const [localStream, setLocalStream] = useState<MediaStream>(connection.getPeerLocalStream().stream());
  const assets = useAssets();
  const { acceptCall, hangupCall, sendAnalytic } = useSocket();

  useEffect(() => {
    const listener = () => {
      if (connection.type === ConferenceTypeEnum.video) {
        setLocalStream(connection.getPeerLocalStream().stream());
      }
    };
    connection.getPeerLocalStream().on('change', listener);

    return () => {
      connection.getPeerLocalStream().off('change', listener);
    };
  }, [connection]);

  return (
    <Box flex={1}>
      {connection.type === ConferenceTypeEnum.video && (
        <>
          <FullCamera deviceManager={connection.getDeviceManager()} localStream={localStream} />
          <Box flex={1}>
            <Background />
            <StatusText variant={'medium'} textColor={'white'} text="در حال تماس تصویری با" />
            <NameText bold variant={'giant'} textColor="white" numberOfLines={1} ellipsizeMode={'tail'} text={name} />
          </Box>
        </>
      )}
      {connection.type === ConferenceTypeEnum.audio && (
        <AudioWhiteBox flex={1}>
          <AudioContainer>
            <AnimationContainer>
              <UserImage url={imageUrl} />
              <AnimationView source={assets.animations.patient_waiting_animation} loop />
            </AnimationContainer>
            <CallingTextView bold variant={'medium'} textColor={'white'} text="در حال تماس با" />
            <NameContainer>
              <AppTextView bold variant={'giant'} textAlign={'center'} numberOfLines={1} ellipsizeMode={'tail'} textColor={'white'} text={name} />
            </NameContainer>
          </AudioContainer>
        </AudioWhiteBox>
      )}
      <Box flex={connection.type === ConferenceTypeEnum.audio ? 1 : undefined} justifyContent={'flex-end'} alignItems={'center'}>
        {incoming && (
          <AnswerButtonsView
            state={'initiating'}
            onAccept={() => {
              sendAnalytic(new Analytics.AbstractCallMetric(connection._id, CallMetricsEventEnum.ACCEPT_CLICKED));
              acceptCall(connection._id, () => connection.start());
            }}
            onReject={() => {
              sendAnalytic(new Analytics.AbstractCallMetric(connection._id, CallMetricsEventEnum.REJECT_CLICKED));
              hangupCall(connection._id);
            }}
          />
        )}
        {!incoming && (
          <AnswerButtonsView
            state={'initiating'}
            onReject={() => {
              sendAnalytic(new Analytics.AbstractCallMetric(connection._id, CallMetricsEventEnum.END_CALL_CLICKED));
              hangupCall(connection._id);
            }}
          />
        )}
      </Box>
    </Box>
  );
}

const Background = styled(Box)`
  width: ${({ theme }) => theme.wp(100)}px;
  height: ${({ theme }) => theme.hp(100)}px;
  background-color: rgba(0, 0, 0, 0.5);
  position: absolute;
  z-index: 2;
`;

const StatusText = styled(AppTextView)`
  position: absolute;
  align-self: center;
  text-align: center;
  bottom: ${({ theme }) => theme.hp(70)}px;
  z-index: 2;
`;

const NameText = styled(AppTextView)`
  position: absolute;
  align-self: center;
  text-align: center;
  bottom: ${({ theme }) => theme.hp(62)}px;
  z-index: 2;
  padding-horizontal: 5%;
`;

const AudioContainer = styled(Box)`
  width: 100%;
  flex: 1;
  background-color: #198b8c;
  justify-content: center;
  align-items: center;
`;

const UserImage = styled(AppAvatar)`
  width: ${hpVal(15)};
  height: ${hpVal(15)};
  align-self: center;
  z-index: 100;
  border-radius: 1000px;
  position: absolute;
`;

const AnimationView = styled(AppLottieView)`
  width: ${hpVal(25)};
  height: ${hpVal(25)};
  align-self: center;
`;

const NameContainer = styled(Box)`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: ${({ theme }) => theme.hp(8)}px;
  background-color: ${({ theme }) => theme.colors.jade};
  align-items: center;
  justify-content: center;
  padding-horizontal: 5%;
`;

const CallingTextView = styled(AppTextView)`
  position: absolute;
  bottom: ${({ theme }) => theme.hp(10)}px;
  align-self: center;
  text-align: center;
`;

const AnimationContainer = styled(Box)`
  width: ${({ theme }) => theme.wp(40)}px;
  height: ${({ theme }) => theme.wp(40)}px;
  align-items: center;
  justify-content: center;
`;

const FullCamera = styled(CameraView)`
  height: 100%;
  width: 100%;
  position: absolute;
`;

const AudioWhiteBox = styled(Box)`
  background-color: white;
`;

export const CallWaitingScreen = React.memo(BaseCallWaitingView);
